import { z } from 'zod';
import { ActorTypeSchema, type ActorType } from '../common/actor.schema';
import { refersTo } from '../common/refers-to';

// The records that own a timeline. `VALUATION_REQUEST` joined with IMM-475.5: a
// valuation's lifecycle entries are linked to the Deal *and* to the request that
// produced them, so the Bewertung tab reads exactly ONE valuation's history in
// its own paginated scope — rather than filtering the Deal's shared feed, where
// a busier Deal pushes the valuation off the loaded page and a re-valuation
// interleaves two requests' steps under one heading.
export const ACTIVITY_SUBJECT_TYPES = ['DEAL', 'LEAD', 'PROPERTY', 'CONTACT', 'VALUATION_REQUEST', 'PROSPECT'] as const;
export const ActivitySubjectTypeSchema = z.enum(ACTIVITY_SUBJECT_TYPES);
export type ActivitySubjectType = z.infer<typeof ActivitySubjectTypeSchema>;

// The activity timeline classifies its actor with the canonical Actor union
// (IMM-208). Deal/lead/etc. activities only ever record MEMBER or SYSTEM, which
// the per-store actor_type CHECK still enforces at the column.
export const ActivityActorTypeSchema = ActorTypeSchema;
export type ActivityActorType = ActorType;

// Which surface wrote the activity (IMM-601.4): the app itself, or an external
// MCP client acting on the bound member's behalf. NOT a new ActorType — the
// actor stays the member; this classifies the surface, following the lead
// module's `api_key_id` attribution precedent. The key id itself is deliberately
// absent from every customer read leaf — Owners see it in the admin call log.
export const ACTIVITY_SOURCES = ['APP', 'CONNECTED_CLIENT'] as const;
export const ActivitySourceSchema = z.enum(ACTIVITY_SOURCES);
export type ActivitySource = z.infer<typeof ActivitySourceSchema>;

// One activity = an event (`contentType`, e.g. `deal.phase-changed`) linked to
// one or more records via `links`. `body` is the denormalised, renderable
// payload — its shape varies by `contentType`, kept loose so the activity store
// stays decoupled from each source's domain shape (ADR 0025). `version > 1`
// means the body was edited.
export const ActivityResponseSchema = z.object({
  id: z.uuid(),
  links: z.array(z.object({ recordType: z.string(), recordId: z.uuid() })),
  contentType: z.string(),
  contentId: z.uuid(),
  actorType: ActivityActorTypeSchema,
  actorId: z.uuid().nullable(),
  // Which surface wrote it (IMM-601.4) — drives the „Über einen verbundenen
  // Client" marker beside the actor. The backend always emits it (every entry
  // written before attribution shipped answers `APP`), and the web-app stamps
  // `APP` on the timeline entries it synthesizes client-side.
  source: ActivitySourceSchema,
  occurredAt: z.iso.datetime(),
  version: z.number().int(),
  body: z.record(z.string(), z.unknown()),
  createdAt: z.iso.datetime(),
});
export type ActivityResponse = z.infer<typeof ActivityResponseSchema>;

export const ListActivitiesPathParamsSchema = z.object({
  organizationId: z.uuid(),
});

/**
 * The page size the activity feeds fall back to when a caller sends no `limit`.
 *
 * Lives on the contract rather than in each read's handler because it is
 * PUBLISHED: the operation descriptions state it, and the developer guide sizes
 * an integrator's paging loop off it. Stated in one place so the number a
 * consumer reads cannot drift from the number the API applies (BL-022). The
 * schema deliberately keeps `limit` optional rather than carrying `.default()` —
 * a default on the schema would change the published request contract.
 */
export const ACTIVITY_PAGE_DEFAULT_LIMIT = 50;

export const ListActivitiesQuerySchema = z.object({
  subjectType: ActivitySubjectTypeSchema,
  subjectId: z.uuid(),
  limit: z.coerce.number().int().min(1).max(100).optional(),
  // Opaque keyset cursor from a previous page's `nextCursor`.
  cursor: z.string().optional(),
});
export type ListActivitiesQuery = z.infer<typeof ListActivitiesQuerySchema>;

export const ListActivitiesResponseSchema = z.object({
  items: z.array(ActivityResponseSchema),
  // Pass back as `?cursor=` for the next page; null when the feed is exhausted.
  nextCursor: z.string().nullable(),
});
export type ListActivitiesResponse = z.infer<typeof ListActivitiesResponseSchema>;

// The `contentType` a scheduled auto-resume projects onto a deal's timeline.
// A manual `Wieder aufnehmen` records the same kind with a MEMBER actor; only
// the SYSTEM-actor ones drive the auto-resume notice (IMM-194).
export const DEAL_RESUMED_CONTENT_TYPE = 'deal.resumed';

// The assignment audit (IMM-402): every change to a deal's responsible-member
// set projects onto the deal timeline under this content type. The `contentId`
// is the event's `assignmentChangeId`, so the projection stays 1:1 with the
// change (replay-once idempotency via the activity `unique(content_type, content_id)`).
export const DEAL_RESPONSIBILITY_CONTENT_TYPE = 'deal.responsibility-changed';

// The valuation lifecycle log (IMM-475.5). Every transition of a valuation
// request projects onto the DEAL's timeline under one of these content types, so
// a single projection serves both the Deal's Aktivität tab and the Bewertung
// tab's filtered view. STABLE — an entry already written keys on the string, so
// renaming one orphans every historical row.
export const VALUATION_ACTIVITY_CONTENT_TYPES = {
  SUBMITTED: 'valuation.request-submitted',
  WITHDRAWN: 'valuation.request-withdrawn',
  TAKEN_ON: 'valuation.request-taken-on',
  SUBMITTED_FOR_APPROVAL: 'valuation.request-submitted-for-approval',
  RETURNED: 'valuation.request-returned',
  RESULT_ADJUSTED: 'valuation.result-adjusted',
  RELEASED: 'valuation.request-released',
} as const;

// The Objekt's change history (IMM-1142). STABLE: a stored entry keys on the string.
// Deliberately NOT part of the activity module's ACTIVITY_CONTENT_TYPES. That set
// drives the MCP tools' enum and body docs, and agent access to these entries is
// a follow-up PRD.
export const PROPERTY_CHANGE_CONTENT_TYPES = {
  STATUS_CHANGED: 'property.status-changed',
  PRICING_SALE_CHANGED: 'property.pricing-sale-changed',
  AREAS_CHANGED: 'property.areas-changed',
  // IMM-1145.3 — the hand mark „Reserviert“: marked, unmarked, or lifted when the Objekt left ACTIVE.
  HAND_RESERVATION_CHANGED: 'property.hand-reservation-changed',
  // IMM-1143.3 — a save of one of the twelve sections, and a Kontakt link's role/note.
  SECTION_CHANGED: 'property.section-changed',
  CONTACT_LINK_CHANGED: 'property.contact-link-changed',
} as const;
export type PropertyChangeContentType =
  (typeof PROPERTY_CHANGE_CONTENT_TYPES)[keyof typeof PROPERTY_CHANGE_CONTENT_TYPES];
export const PROPERTY_CHANGE_CONTENT_TYPE_LIST: ReadonlyArray<PropertyChangeContentType> =
  Object.values(PROPERTY_CHANGE_CONTENT_TYPES);

// The shared prefix, used to group these entries under ONE „Bewertung" chip in
// the Deal's activity-type filter without a per-type allow-list to maintain. NOT
// how the Bewertung tab selects its entries — that reads the `VALUATION_REQUEST`
// subject scope, so it is bounded to one valuation and cannot be truncated by
// the Deal feed's paging.
export const VALUATION_ACTIVITY_CONTENT_TYPE_PREFIX = 'valuation.';

// One unseen auto-resume notice: the system auto-resume entry on a deal the
// caller deferred and has not yet acknowledged. `resumedAt` is the auto-resume
// instant ({Datum} in the notice copy). `activityId` is POSTed back to mark-seen.
export const AutoResumeNoticeSchema = z.object({
  activityId: z.uuid(),
  dealId: refersTo('deal', z.uuid()),
  resumedAt: z.iso.datetime(),
});
export type AutoResumeNotice = z.infer<typeof AutoResumeNoticeSchema>;

export const ListAutoResumeNoticesPathParamsSchema = z.object({
  organizationId: z.uuid(),
});

export const ListAutoResumeNoticesQuerySchema = z.object({
  dealId: z.uuid(),
});
export type ListAutoResumeNoticesQuery = z.infer<typeof ListAutoResumeNoticesQuerySchema>;

export const ListAutoResumeNoticesResponseSchema = z.object({
  items: z.array(AutoResumeNoticeSchema),
});
export type ListAutoResumeNoticesResponse = z.infer<typeof ListAutoResumeNoticesResponseSchema>;

export const ListMemberActivitiesPathParamsSchema = z.object({
  workspaceId: z.uuid(),
  memberId: z.uuid(),
});

export const ListMemberActivitiesQuerySchema = z.object({
  limit: z.coerce.number().int().min(1).max(100).optional(),
  cursor: z.string().optional(),
});

export const MarkActivitySeenPathParamsSchema = z.object({
  organizationId: z.uuid(),
  activityId: z.uuid(),
});

export const MarkActivitySeenResponseSchema = z.object({
  activityId: z.uuid(),
});
export type MarkActivitySeenResponse = z.infer<typeof MarkActivitySeenResponseSchema>;
